import {
  type FormEvent,
  type KeyboardEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Braces,
  Check,
  ChevronDown,
  Copy,
  HelpCircle,
  Maximize2,
  MessageSquare,
  Minimize2,
  PanelRight,
  PanelRightClose,
  Send,
  Sparkles,
  X,
} from 'lucide-react';
import {
  useAdvisorChatSession,
  useAdvisorQuestionSuggestions,
  useSendAdvisorMessage,
} from '@/hooks/queries';
import type { ChatMessage } from '@/api/advisorChat';
import type { RxAssistantMode } from '@/hooks/useRxAssistantMode';
import { AssistantQuickActions } from '@/components/AssistantQuickActions';
import { getAssistantContext } from '@/lib/assistantContext';
import { usePatientContext } from '@/hooks/usePatientContext';
import { formatPatientContext } from '@/lib/patientContext';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  formatAdvisorChatMessageHtml,
  messageContentToPlainTextForCopy,
} from '@/lib/advisorChatMarkdown';
import { cn } from '@/lib/utils';

/**
 * RxAssistant — V2 port of v1's `AdvisorChatPopper`
 * (clinician/src/components/AdvisorChatPopper.tsx).
 *
 * Renders in one of three modes (controlled via `mode` / `onModeChange`
 * props, persisted by `useRxAssistantMode`):
 *
 *   'closed' — only the floating launcher button is visible
 *   'popper' — floating overlay in the bottom-right (default behavior)
 *   'panel'  — docked into PatientShell's right column as a block element
 *
 * The component instance stays mounted across mode changes — its internal
 * state (sessionId, message log, draft text) survives a toggle so the user
 * doesn't lose an in-progress conversation when they dock/undock.
 *
 * Differences from v1:
 *   • Native Tailwind UI instead of `@chatscope/chat-ui-kit-react`
 *   • lucide-react icons instead of `@ant-design/icons`
 *   • Markdown rendering still goes through marked + DOMPurify
 *   • Adds the panel-mode docking that v1 lacks (v1 was popper-only)
 *
 * Mounted once in PatientShell; gated by clinician flag
 * `clinic_enable_clinician_advisor_chat` upstream.
 */
type Props = {
  patientId: number;
  mode: RxAssistantMode;
  onModeChange: (next: RxAssistantMode) => void;
  /** Active page's context key — drives the quick-action chip rack
   *  above the composer. When omitted (or when no chips are defined
   *  for the key), the rack renders nothing. */
  contextKey?: string;
};

export const RxAssistantChat = ({ patientId, mode, onModeChange, contextKey }: Props) => {
  const { t } = useTranslation();
  const { context: patientContext } = usePatientContext(patientId);
  const [contextCopied, setContextCopied] = useState(false);
  const [searchParams] = useSearchParams();
  const startQuery = searchParams.get('start');
  const endQuery = searchParams.get('end');

  // Expand toggle only applies in popper mode; in panel mode the panel
  // already fills the docked column so there's nothing to expand to.
  const [isExpanded, setIsExpanded] = useState(false);
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [sessionId, setSessionId] = useState<number | null>(null);
  // Mirror v1's `dateChanged` flag: if the user changed the date filter while
  // a message was in flight, don't latch onto the returned sessionId — it
  // belongs to the OLD context and would mix two date ranges in one thread.
  const [dateChanged, setDateChanged] = useState(false);

  // Optimistic message log. Sorted newest-first to match v1's MessageList
  // reverse() pattern (server returns newest-first too).
  const [localMessages, setLocalMessages] = useState<ChatMessage[] | null>(null);
  const [copiedMessageId, setCopiedMessageId] = useState<number | null>(null);
  const [draft, setDraft] = useState('');
  const lastMessageRef = useRef<HTMLDivElement>(null);

  const sessionQuery = useAdvisorChatSession(patientId, {
    sessionId,
    subjectType: 'general',
  });
  const sendMessage = useSendAdvisorMessage(patientId);
  const suggestionsQuery = useAdvisorQuestionSuggestions(patientId, {
    subjectType: 'general',
  });

  // Bootstrap localMessages from the loaded session (one-shot per session).
  useEffect(() => {
    if (sessionQuery.data && localMessages === null) {
      setLocalMessages(sessionQuery.data.messages);
    }
  }, [sessionQuery.data, localMessages]);

  // Reset session whenever the date-filter context changes (URL ?start/?end).
  // v1 does the same to keep the LLM's view of the patient's data scoped to
  // whatever the clinician is currently looking at.
  useEffect(() => {
    setSessionId(null);
    setLocalMessages(null);
    if (sendMessage.isPending) setDateChanged(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startQuery, endQuery]);

  // Auto-scroll to the latest message. Use rAF so the DOM has flushed.
  useEffect(() => {
    requestAnimationFrame(() => {
      lastMessageRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    });
  }, [localMessages, sendMessage.isPending]);

  const handleSend = async (text: string, displayText?: string) => {
    const trimmed = text.trim();
    if (!trimmed || sendMessage.isPending) return;

    // Quick-action chips pass `displayText` to keep the chat bubble
    // clean (just the chip label) while the actual prompt sent to
    // the model includes the JSON context payload. Normal typed
    // messages use `text` for both.
    const bubbleText = (displayText ?? text).trim();

    const now = new Date();
    const optimistic: ChatMessage = {
      id: now.getTime(),
      sessionId: sessionId ?? 0,
      timestamp: now.toISOString(),
      role: 'user',
      content: bubbleText,
      contentType: 'text/plain',
      isOptimistic: true,
    };
    setLocalMessages((prev) => [optimistic, ...(prev ?? [])]);
    setDraft('');

    try {
      // Attach the standardized patient context to the FIRST message of a
      // session (session-create) only. The server threads follow-ups into the
      // same session, so the model keeps this context for the rest of the
      // conversation — no need to re-send it on every turn. This is what makes
      // even a free-typed question context-aware, from any page. `bubbleText`
      // stays clean so the context block never shows in the chat UI.
      const isNewSession = !sessionId;
      let contentToSend = trimmed;
      if (isNewSession) {
        // Base = the standardized patient context (same on every page). On the
        // Logs pages, add the page's richer per-day food-log digest on top —
        // "increase the context when we're on the meal page" — reusing the
        // already-capped snapshot the Logs views register.
        const isMealPage = contextKey?.startsWith('logs-') ?? false;
        const snap = isMealPage && contextKey ? getAssistantContext(contextKey) : null;
        const mealDetail = snap
          ? `\n\n<meal_detail source="${snap.title}">\n${JSON.stringify(snap.data, null, 2)}\n</meal_detail>`
          : '';
        contentToSend = `${formatPatientContext(patientContext)}${mealDetail}\n\n${trimmed}`;
      }
      const reply = await sendMessage.mutateAsync({
        sessionId,
        content: contentToSend,
        subjectType: 'general',
        context: {
          filterDatesGte: startQuery,
          filterDatesLte: endQuery,
        },
      });
      setLocalMessages((prev) => [reply, ...(prev ?? [])]);
      // Latch onto the new session id so subsequent messages thread into it.
      // Don't latch if the user changed the date filter mid-flight (the
      // returned id belongs to the old context).
      if (!sessionId && !dateChanged) {
        setSessionId(reply.sessionId);
      }
      setDateChanged(false);
    } catch {
      // Roll back the optimistic user message on failure.
      setLocalMessages((prev) =>
        (prev ?? []).filter((m) => !(m.isOptimistic && m.id === optimistic.id)),
      );
    }
  };

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    handleSend(draft);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    // Enter sends; Shift+Enter inserts a newline.
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend(draft);
    }
  };

  // Copy the exact patient context RxAssistant is given — the standardized
  // package (demographics, motivations, restrictions, goals, CGM, metrics) plus
  // any page-specific snapshot for the current view. Lets a clinician see
  // precisely what the model can reason over.
  const handleCopyContext = async () => {
    const snap = contextKey ? getAssistantContext(contextKey) : null;
    const pageBlock = snap
      ? `\n\n<context source="${snap.title}">\n${JSON.stringify(snap.data, null, 2)}\n</context>`
      : '';
    const payload = `${formatPatientContext(patientContext)}${pageBlock}`;
    try {
      await navigator.clipboard.writeText(payload);
      setContextCopied(true);
      setTimeout(() => setContextCopied(false), 2000);
    } catch {
      // Clipboard rejected (browser policy / no perms) — silent.
    }
  };

  const handleCopy = async (msg: ChatMessage) => {
    try {
      const text = messageContentToPlainTextForCopy(msg);
      await navigator.clipboard.writeText(text);
      setCopiedMessageId(msg.id);
      setTimeout(() => setCopiedMessageId(null), 3000);
    } catch {
      // Clipboard rejected (browser policy / no perms) — silent; user can retry.
    }
  };

  const messages = localMessages ?? [];
  const mostRecent = messages[0];
  const isThinking =
    sendMessage.isPending && (!mostRecent || mostRecent.role !== 'assistant');

  // Suggestion chips: prefer server-provided; fall back to a curated list
  // (mirrors v1's static fallback in `getAdvisorCommonQuestions`).
  const suggestions = useMemo(() => {
    const fromServer = suggestionsQuery.data ?? [];
    if (fromServer.length > 0) return fromServer.slice(0, 4).map((s) => s.question);
    return [
      t('Write a progress note for me'),
      t('What are high-protein foods this patient ate, and how can we increase it?'),
      t('What are high-fibre foods this patient ate, and how can we increase them?'),
      t('What are three suggestions I can provide this patient based on their data?'),
    ];
  }, [suggestionsQuery.data, t]);

  // Chat body — identical in popper and panel modes; only the wrapper around
  // it changes positioning + sizing. Extracted as a local element so the JSX
  // tree below stays scannable.
  const chatBody = (
    <>
      {/* Header */}
      <div className="flex items-center gap-2 border-b bg-primary px-3 py-2 text-primary-foreground">
        <Sparkles className="h-4 w-4" />
        <span className="text-sm font-semibold">RxAssistant</span>
        <div className="ml-auto flex items-center gap-1">
          {/* Copy the patient context the assistant is given, for this patient.
              Available to every RxAssistant user (the component itself is
              gated by the advisor-chat flag upstream). */}
          <button
            type="button"
            onClick={handleCopyContext}
            className="rounded p-1 transition-colors hover:bg-primary-foreground/10"
            aria-label={t('Copy patient context')}
            title={t('Copy the patient context sent to RxAssistant')}
          >
            {contextCopied ? <Check className="h-4 w-4" /> : <Braces className="h-4 w-4" />}
          </button>
          {/* Expand toggle is popper-only — in panel mode the panel already
              fills its column so there's nothing to expand to. */}
          {mode === 'popper' && (
            <button
              type="button"
              onClick={() => setIsExpanded((v) => !v)}
              className="rounded p-1 transition-colors hover:bg-primary-foreground/10"
              aria-label={isExpanded ? t('Collapse') : t('Expand')}
              title={isExpanded ? t('Collapse') : t('Expand')}
            >
              {isExpanded ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
            </button>
          )}
          {/* Dock / undock toggle. PanelRight icon when currently in popper
              (action = move to side); PanelRightClose when docked (action =
              pop out as floating). The icon represents the *current* layout
              going away, which reads more intuitively than "what action
              will happen". */}
          <button
            type="button"
            onClick={() => onModeChange(mode === 'panel' ? 'popper' : 'panel')}
            className="rounded p-1 transition-colors hover:bg-primary-foreground/10"
            aria-label={mode === 'panel' ? t('Pop out') : t('Dock to side')}
            title={mode === 'panel' ? t('Pop out') : t('Dock to side')}
          >
            {mode === 'panel' ? (
              <PanelRightClose className="h-4 w-4" />
            ) : (
              <PanelRight className="h-4 w-4" />
            )}
          </button>
          <button
            type="button"
            onClick={() => onModeChange('closed')}
            className="rounded p-1 transition-colors hover:bg-primary-foreground/10"
            aria-label={t('Close')}
            title={t('Close')}
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Body — message list (newest at bottom; we stored newest-first
          and reverse here so render order matches what the user expects). */}
      <div className="flex-1 overflow-y-auto bg-muted/20 p-3">
        {messages.length === 0 ? (
          <WelcomeState
            suggestions={suggestions}
            onSuggestionClick={(q) => handleSend(q)}
            onDisclaimerClick={() => setShowDisclaimer(true)}
          />
        ) : (
          <>
            <DisclaimerBanner onClick={() => setShowDisclaimer(true)} />
            <div className="space-y-3">
              {[...messages].reverse().map((msg) => (
                <MessageBubble
                  key={msg.id}
                  message={msg}
                  copied={copiedMessageId === msg.id}
                  onCopy={() => handleCopy(msg)}
                />
              ))}
              {isThinking && <ThinkingIndicator label={t('RxAssistant is thinking…')} />}
              <div ref={lastMessageRef} />
            </div>
          </>
        )}
      </div>

      {/* Quick-action chips — page-context aware, only render when
          the active tab registered a snapshot AND has chips defined.
          Sit just above the composer to match Cursor/Copilot patterns;
          small pills so they don't dominate the chat chrome. */}
      {contextKey && (
        <AssistantQuickActions
          contextKey={contextKey}
          onChip={(displayText, promptText) => handleSend(promptText, displayText)}
          disabled={sendMessage.isPending}
        />
      )}

      {/* Composer */}
      <form
        onSubmit={onSubmit}
        className="flex items-end gap-2 border-t bg-card p-2"
      >
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder={t('Type a message…')}
          rows={1}
          disabled={sendMessage.isPending}
          className="max-h-32 min-h-[36px] flex-1 resize-none rounded-md border border-input bg-background px-2.5 py-1.5 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-60"
        />
        <Button
          type="submit"
          size="icon"
          disabled={!draft.trim() || sendMessage.isPending}
          aria-label={t('Send')}
        >
          <Send className="h-4 w-4" />
        </Button>
      </form>
    </>
  );

  return (
    <>
      {/* Floating launcher — visible only when chat is closed. Reopens to
          whatever mode the user last had (defaults to popper). */}
      {mode === 'closed' && (
        <button
          type="button"
          onClick={() => onModeChange('popper')}
          className="fixed bottom-4 right-4 z-40 flex h-14 items-center gap-2 rounded-full bg-primary px-5 text-sm font-medium text-primary-foreground shadow-lg transition-transform hover:scale-105 hover:shadow-xl"
          aria-label={t('Open RxAssistant')}
        >
          <Sparkles className="h-5 w-5" />
          {t('RxAssistant')}
        </button>
      )}

      {/* Popper mode — fixed floating overlay, position:fixed. v1's
          original behaviour: bottom-right, expandable. */}
      {mode === 'popper' && (
        <div
          className={cn(
            'fixed bottom-4 right-4 z-40 flex flex-col overflow-hidden rounded-lg border bg-card shadow-2xl',
            // Capped at 75vh per UX feedback — taller versions ate the
            // entire viewport on small laptops and crowded out the page
            // content underneath.
            isExpanded
              ? 'h-[75vh] w-[min(50vw,720px)] min-w-[360px]'
              : 'h-[75vh] w-[min(28vw,460px)] min-w-[320px]',
          )}
          role="dialog"
          aria-label={t('RxAssistant chat')}
        >
          {chatBody}
        </div>
      )}

      {/* Panel mode — docked into PatientShell's right column. Uses sticky
          positioning so it stays in view as the user scrolls the patient
          content on the left. Height = viewport minus a small offset so it
          aligns with the top of the page header. */}
      {mode === 'panel' && (
        // `flex-1` fills this aside to the height of its grid cell — which
        // PatientShell stretches (flex-col + self-stretch) to the full row
        // height. That tall containing block is what gives the sticky panel
        // below room to travel: it stays pinned near the top while the tab
        // content scrolls past in <main>, instead of scrolling away.
        <aside
          className="min-h-0 flex-1"
          role="complementary"
          aria-label={t('RxAssistant chat')}
        >
          {/* Capped at 75vh per UX feedback — at 100vh the panel ran the
              full screen and crowded the page underneath. Sticky offset
              matches the patient-rail's top-4 spacing for visual alignment. */}
          <div className="sticky top-4 z-30 flex h-[75vh] flex-col overflow-hidden rounded-lg border bg-card shadow-md">
            {chatBody}
          </div>
        </aside>
      )}

      <DisclaimerDialog
        open={showDisclaimer}
        onClose={() => setShowDisclaimer(false)}
      />
    </>
  );
};

/* -------------------------------------------------------------------------- */

const WelcomeState = ({
  suggestions,
  onSuggestionClick,
  onDisclaimerClick,
}: {
  suggestions: string[];
  onSuggestionClick: (q: string) => void;
  onDisclaimerClick: () => void;
}) => {
  const { t } = useTranslation();
  return (
    <div className="flex h-full flex-col items-stretch justify-center gap-4 px-2 py-4">
      <div className="flex flex-col items-center gap-2 text-center">
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
          <MessageSquare className="h-6 w-6" />
        </div>
        <p className="text-base font-semibold">{t('How can I help you today?')}</p>
        <p className="text-xs text-muted-foreground">
          {t("Ask me anything about the patient's data, or try one of these:")}
        </p>
      </div>
      <div className="space-y-1.5">
        {suggestions.map((q) => (
          <button
            key={q}
            type="button"
            onClick={() => onSuggestionClick(q)}
            className="flex w-full items-center gap-2 rounded-md border bg-card px-3 py-2 text-left text-xs transition-colors hover:bg-muted"
          >
            <ChevronDown className="h-3 w-3 shrink-0 -rotate-90 text-muted-foreground" />
            <span className="line-clamp-2">{q}</span>
          </button>
        ))}
      </div>
      <DisclaimerBanner onClick={onDisclaimerClick} />
    </div>
  );
};

const DisclaimerBanner = ({ onClick }: { onClick: () => void }) => {
  const { t } = useTranslation();
  return (
    <p className="mb-3 rounded-md border border-amber-200 bg-amber-50 px-2 py-1.5 text-[0.6875rem] text-amber-900">
      {t('RxAssistant can make mistakes. Review the')}{' '}
      <button
        type="button"
        onClick={onClick}
        className="font-semibold underline underline-offset-2 hover:no-underline"
      >
        {t('disclaimer')}
      </button>{' '}
      {t('before continuing.')}
    </p>
  );
};

const MessageBubble = ({
  message,
  copied,
  onCopy,
}: {
  message: ChatMessage;
  copied: boolean;
  onCopy: () => void;
}) => {
  const { t } = useTranslation();
  const isUser = message.role === 'user';
  return (
    <div className={cn('flex w-full min-w-0', isUser ? 'justify-end' : 'justify-start')}>
      <div
        className={cn(
          // `min-w-0` keeps flex children from forcing the bubble wider than
          // its `max-w-[85%]` cap; `overflow-hidden` clips any element that
          // ignores the cap (e.g. a scrollable <pre> renders its own
          // horizontal scrollbar inside the bubble instead).
          'min-w-0 max-w-[85%] overflow-hidden rounded-lg px-3 py-2 text-sm shadow-sm',
          isUser ? 'bg-primary text-primary-foreground' : 'bg-card border',
          message.isOptimistic && 'opacity-70',
        )}
      >
        {/* Both branches use sanitized HTML so markdown in assistant
            replies renders properly while user messages stay literal
            (escapePlainTextForHtml runs in the renderer).
            ────────────────────────────────────────────────────────────
            Wrapping rules below address the "overflow past the bubble"
            issue seen in testing:
              - `break-words` + arbitrary `overflow-wrap:anywhere` for
                unbreakable runs (long URLs, hashes, base64)
              - <pre> blocks scroll horizontally instead of pushing wider
              - <code> inline pieces wrap mid-word
              - <table> wraps in a scrollable block container
              - <img> shrinks to bubble width */}
        <div
          className="rx-md w-full min-w-0 break-words text-sm leading-relaxed [overflow-wrap:anywhere] [&_a]:break-words [&_a]:underline [&_code]:break-words [&_code]:rounded [&_code]:bg-muted/60 [&_code]:px-1 [&_code]:py-0.5 [&_code]:text-[0.75rem] [&_img]:h-auto [&_img]:max-w-full [&_li]:my-0.5 [&_ol]:list-decimal [&_ol]:pl-5 [&_p+p]:mt-2 [&_pre]:max-w-full [&_pre]:overflow-x-auto [&_pre]:rounded [&_pre]:bg-muted/60 [&_pre]:p-2 [&_pre]:text-[0.75rem] [&_pre]:leading-snug [&_pre_code]:bg-transparent [&_pre_code]:p-0 [&_strong]:font-semibold [&_table]:block [&_table]:max-w-full [&_table]:overflow-x-auto [&_table]:whitespace-nowrap [&_ul]:list-disc [&_ul]:pl-5"
          dangerouslySetInnerHTML={{ __html: formatAdvisorChatMessageHtml(message) }}
        />
        {!isUser && (
          <div className="mt-1.5 flex justify-end">
            <button
              type="button"
              onClick={onCopy}
              className="inline-flex items-center gap-1 text-[0.625rem] text-muted-foreground transition-colors hover:text-foreground"
            >
              {copied ? (
                <>
                  <Check className="h-3 w-3" />
                  {t('Copied')}
                </>
              ) : (
                <>
                  <Copy className="h-3 w-3" />
                  {t('Copy')}
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const ThinkingIndicator = ({ label }: { label: string }) => (
  <div className="flex items-center gap-2 px-1 text-xs text-muted-foreground">
    <span className="flex gap-1">
      <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-current [animation-delay:-0.3s]" />
      <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-current [animation-delay:-0.15s]" />
      <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-current" />
    </span>
    <span>{label}</span>
  </div>
);

/**
 * Disclaimer dialog — verbatim copy of v1's disclaimer text. Mounted at
 * the root so it renders above the chat panel, not inside it (otherwise
 * the panel's overflow:hidden would clip a long disclaimer on narrow
 * widths).
 */
const DisclaimerDialog = ({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) => {
  const { t } = useTranslation();
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <HelpCircle className="h-5 w-5 text-primary" />
            {t('RxAssistant Disclaimer')}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm leading-relaxed">
          <p>
            <strong>{t('Important Notice:')}</strong>{' '}
            {t(
              'Please read this disclaimer carefully before using the RxAssistant within the RxFood Clinician Portal.',
            )}
          </p>
          <p>
            {t(
              'The RxAssistant (e.g., "AI Clinical Assistant," "Automated Documentation Aid," "Smart Suggestion Engine") is an artificial intelligence-powered tool designed to assist healthcare professionals in functions such as documentation, providing quick access to relevant patient data, generating meal plans, etc.',
            )}
          </p>
          <p className="font-semibold">{t('Key Limitations and User Responsibilities:')}</p>
          <ol className="list-decimal space-y-2 pl-5">
            <li>
              <strong>{t('Assistance Tool Only:')}</strong>{' '}
              {t(
                'The RxAssistant is an assistance tool and not a substitute for professional medical judgment, clinical expertise, or direct patient care. All information, suggestions, or outputs generated by this agent are for informational and facilitative purposes only.',
              )}
            </li>
            <li>
              <strong>{t('No Diagnostic or Treatment Authority:')}</strong>{' '}
              {t(
                'This agent does not diagnose, treat, or provide medical advice. Clinical decisions, diagnoses, treatment plans, and patient management remain the sole responsibility of the qualified healthcare professional.',
              )}
            </li>
            <li>
              <strong>{t('Accuracy and Verification:')}</strong>{' '}
              {t(
                'While efforts are made to ensure the accuracy and relevance of the information processed by the agent, its outputs may contain errors, omissions, or inaccuracies. Healthcare professionals are solely responsible for verifying the accuracy, completeness, and appropriateness of all information generated by or presented through this agent before relying on it or incorporating it into patient records or care.',
              )}
            </li>
            <li>
              <strong>{t('Data Source Limitations:')}</strong>{' '}
              {t(
                "The agent's outputs are based on the data available within RxFood clinician portal and its underlying algorithms. It may not account for all relevant clinical context, new information not yet entered, or nuances of individual patient cases.",
              )}
            </li>
            <li>
              <strong>{t('Continuous Learning and Evolution:')}</strong>{' '}
              {t(
                'AI models may evolve over time. The performance and outputs of the RxAssistant may change as it learns and is updated.',
              )}
            </li>
            <li>
              <strong>{t('User Accountability:')}</strong>{' '}
              {t(
                'By using the RxAssistant, you acknowledge and agree that you are ultimately responsible for all patient care decisions, documentation, and compliance with all applicable laws, regulations, and professional standards.',
              )}
            </li>
          </ol>
          <p className="font-semibold">
            {t(
              'By proceeding to use the RxAssistant, you acknowledge that you have read, understood, and agree to the terms of this disclaimer.',
            )}
          </p>
          <div className="flex justify-end pt-2">
            <Button onClick={onClose}>{t('I understand')}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
